import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import QRCode from 'qrcode';
import { getFontEmbedCSS, toPng } from 'html-to-image';
import { Download } from 'lucide-react';
import { Toggle } from '@kioskgaming/ui';
import ShopPromoPoster, { POSTER_BACKGROUND, POSTER_HEIGHT, POSTER_WIDTH } from './ShopPromoPoster';
import { listShopCampaignBonuses } from '../../services/api';
import type { CampaignBonus } from '../../pages/depositBonusDisplay';
import { loadPosterShowBonus, pickPosterCampaign, depositBonusForPoster, savePosterShowBonus } from './posterDepositBonus';

type ShopPosterDownloadSectionProps = {
  /** Full kiosk URL, e.g. https://myshop.example.com */
  kioskUrl: string | null;
};

/** The A4 poster at half size: about 400px wide, readable without leaving the page. */
const PREVIEW_SCALE = 0.5;

export default function ShopPosterDownloadSection({ kioskUrl }: ShopPosterDownloadSectionProps) {
  const captureRef = useRef<HTMLDivElement>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  // The Shop's deposit bonus campaigns: undefined while loading, null when they could not be loaded.
  const [campaigns, setCampaigns] = useState<CampaignBonus[] | null | undefined>(undefined);
  // Whether the Shop wants its running deposit bonus on the poster (kept on this device, per kiosk URL).
  const [showBonus, setShowBonus] = useState<boolean>(() => (kioskUrl ? loadPosterShowBonus(kioskUrl) : true));

  const displayUrl = kioskUrl ?? '';

  useEffect(() => {
    if (kioskUrl) setShowBonus(loadPosterShowBonus(kioskUrl));
  }, [kioskUrl]);

  useEffect(() => {
    if (!kioskUrl) return;
    let cancelled = false;
    listShopCampaignBonuses()
      .then((res) => {
        if (!cancelled) setCampaigns(res?.success && res.data ? res.data.campaigns ?? [] : null);
      })
      .catch(() => {
        if (!cancelled) setCampaigns(null);
      });
    return () => {
      cancelled = true;
    };
  }, [kioskUrl]);

  const runningCampaign = useMemo(() => (campaigns ? pickPosterCampaign(campaigns) : null), [campaigns]);
  const posterOffer = runningCampaign && showBonus ? depositBonusForPoster(runningCampaign) : null;

  const updateShowBonus = (next: boolean) => {
    setShowBonus(next);
    if (kioskUrl) savePosterShowBonus(kioskUrl, next);
  };

  useEffect(() => {
    if (!kioskUrl) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    // Larger than it is shown: the PNG is exported at twice the poster's size, and a QR code
    // stretched from a small image prints soft.
    QRCode.toDataURL(kioskUrl, {
      width: 720,
      margin: 1,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [kioskUrl]);

  const canDownload = !!kioskUrl && !!qrDataUrl && campaigns !== undefined;

  const handleDownloadPng = useCallback(async () => {
    const node = captureRef.current;
    if (!node || !kioskUrl) {
      toast.error('Kiosk URL is not ready');
      return;
    }
    setDownloading(true);
    try {
      await document.fonts.ready;
      let fontEmbedCSS: string | undefined;
      try {
        fontEmbedCSS = await getFontEmbedCSS(node, { cacheBust: true });
      } catch {
        fontEmbedCSS = undefined;
      }

      const dataUrl = await toPng(node, {
        cacheBust: true,
        pixelRatio: 2,
        width: POSTER_WIDTH,
        height: POSTER_HEIGHT,
        backgroundColor: POSTER_BACKGROUND,
        fontEmbedCSS,
        style: {
          transform: 'none',
          margin: '0',
        },
      });

      const link = document.createElement('a');
      link.download = 'vault-play-poster.png';
      link.href = dataUrl;
      link.click();
      toast.success('Poster downloaded');
    } catch {
      toast.error('Could not generate poster image');
    } finally {
      setDownloading(false);
    }
  }, [kioskUrl]);

  if (!kioskUrl) {
    return null;
  }

  return (
    <div className="mt-8 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-stone-900">Download promo poster</h2>
          <p className="mt-1 max-w-xl text-sm text-stone-600">
            A4 portrait PNG for printing or sharing. Includes your kiosk QR code and URL (
            <span className="font-mono text-xs text-stone-800">{displayUrl}</span>
            ).
          </p>
        </div>
        <button
          type="button"
          disabled={!canDownload || downloading}
          onClick={() => void handleDownloadPng()}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download className="h-4 w-4" aria-hidden />
          {downloading ? 'Generating…' : 'Download PNG'}
        </button>
      </div>

      {campaigns !== undefined ? (
        <div className="mt-5 grid max-w-xl gap-4 rounded-lg border border-stone-200 bg-stone-50 p-4">
          {runningCampaign ? (
            <div className="grid gap-1">
              <Toggle
                id="shop-poster-show-bonus"
                label="Show the deposit bonus on the poster"
                checked={showBonus}
                onChange={updateShowBonus}
                disabled={downloading}
              />
              <p className="text-xs text-stone-500">From your running campaign “{runningCampaign.name}”.</p>
            </div>
          ) : campaigns === null ? (
            <p className="text-sm text-stone-600">
              Could not check your deposit bonus campaigns, so the poster is shown without a bonus.
            </p>
          ) : (
            <p className="text-sm text-stone-600">
              No deposit bonus campaign is running. Start one in{' '}
              <Link
                to="/deposit-bonus"
                className="font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
              >
                Deposit bonus campaign
              </Link>{' '}
              to show it on the poster.
            </p>
          )}
        </div>
      ) : null}

      <p className="mt-3 text-xs text-stone-500">
        Tip: open the PNG and print, or use your browser’s print dialog on this page for PDF.
      </p>

      <div className="mt-4 overflow-x-auto rounded-lg border border-stone-100 bg-[#14161F] p-4">
        <div
          className="mx-auto overflow-hidden"
          style={{ width: POSTER_WIDTH * PREVIEW_SCALE, height: POSTER_HEIGHT * PREVIEW_SCALE }}
        >
          <div style={{ transform: `scale(${PREVIEW_SCALE})`, transformOrigin: 'top left' }}>
            <ShopPromoPoster
              ref={captureRef}
              kioskUrl={displayUrl}
              qrDataUrl={qrDataUrl}
              bonus={posterOffer}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
